import {
  Button,
  Checkbox,
  CloseButton,
  Group,
  Input,
  LoadingOverlay,
  Modal,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { DatesRangeValue } from '@mantine/dates';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { closeAllModals, openModal } from '@mantine/modals';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router';
import {
  DatasetsFilesInfo,
  delDatasetsFiles,
  getDatasetsFilesList,
  ListFileStatus,
  ListResponse,
  refreshDatasetsWebsite,
} from '@mygpt/app/datasets/services';
import { getWebConnectorFiles } from '@mygpt/app/apis/services/website';
import { SvgSearch, SvgTrash } from '@/components/Common/icon';
import { SvgCalenderGray } from '@/components/Dashboard/icon';
import { EditForm } from '@/components/Datasets/DataSources/form';
import Th from '@/components/Datasets/DataSources/th';
import DateSelector from '@/components/DateSelector';
import SelectedItemRemove from '@/components/Embeddings/selectedItemRemove';
import TableLoading from '@/components/TableLoading';
import { useDatasetStore } from '@/store/dataset';
import TableItem from './Item';
import classes from './list.module.css';
import { AdvancedTrainModal } from '@/components/Datasets/AdvancedTrainModal';
import { removeSearchParam } from '@/utils/tool';
import { isFileStatusProcess, isFileStatusReady } from '@/utils/embed';

const InitPageInfo = {
  page: 1,
  pages: 1,
  size: 50,
  total: 0,
};

export const SiteList = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryStatus = searchParams.get('status') as ListFileStatus;
  const { '*': type, datasetId: datasetIdParam, connectorId: connectorIdParam } = useParams();
  const connectorId = connectorIdParam as string;
  const datasetId = datasetIdParam as string;
  let fileType = type as string;
  // 检查是否为website-pro路径
  const isWebsitePro = location.pathname.includes('/web-connector/');
  // 对于website-pro，将fileType设置为website以保持兼容性
  if (isWebsitePro) {
    fileType = 'website';
  }
  const matches = useMediaQuery('(min-width: 640px)');
  const [initLoading, setInitLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const { isCloning, setCloningTipOpened, setCount } = useDatasetStore();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [deleteFileIds, setDeleteFileIds] = useState<string[]>([]);
  const [fileList, setFileList] = useState<DatasetsFilesInfo[]>([]);
  const [filesListPageInfo, setFilesListPageInfo] = useState<ListResponse>(InitPageInfo);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 200);
  const [statusValue, setStatusValue] = useState<ListFileStatus | ''>(queryStatus || '');
  const [rangeDate, setRangeDate] = useState<string[]>(['', '']);
  const listTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleDateChange = (date: DatesRangeValue | null) => {
    let dateTime: string[] = ['', ''];
    if (date) {
      dateTime = date.map((item) => (item && dayjs(item).format('YYYY-MM-DD HH:mm:ss')) || '');
    }

    setRangeDate(dateTime);
  };

  const [sortBy, setSortBy] = useState<string>('');
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const setSorting = (field: string) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
  };

  const [editItem, setEditItem] = useState<DatasetsFilesInfo>();
  const [editModalOpened, setEditModalOpened] = useState(false);

  const allSelected = useMemo(
    () => selectedIds.length > 0 && fileList?.length === selectedIds.length,
    [fileList, selectedIds]
  );

  const isFileDeleteLoading = (fileId: string) => {
    return deleteFileIds.indexOf(fileId) !== -1;
  };

  const toggleAllChecked = useCallback(
    (checked: boolean) => {
      setSelectedIds(checked ? fileList.map((item) => item.id) : []);
    },
    [fileList]
  );

  const selectItem = useCallback(
    (itemId: string, shiftKey: boolean = false) => {
      const currentIndex = fileList.findIndex((item) => item.id === itemId);

      // Shift 键多选逻辑
      if (shiftKey && lastSelectedIndex !== null && currentIndex !== -1) {
        const startIndex = Math.min(lastSelectedIndex, currentIndex);
        const endIndex = Math.max(lastSelectedIndex, currentIndex);
        const idsInRange = fileList.slice(startIndex, endIndex + 1).map((item) => item.id);

        setSelectedIds((prevIds) => {
          const newIds = new Set(prevIds);
          idsInRange.forEach((id) => newIds.add(id));
          return Array.from(newIds);
        });
        return;
      }

      // 普通点击逻辑
      setSelectedIds((ids) => {
        if (ids.includes(itemId)) {
          setLastSelectedIndex(null);
          return ids.filter((id) => id !== itemId);
        }
        setLastSelectedIndex(currentIndex);
        return [...ids, itemId];
      });
    },
    [fileList, lastSelectedIndex]
  );

  const fileListPageOnchange = (page: number) => {
    if (page !== filesListPageInfo.page) {
      // 立即更新页码状态，让 UI 即时响应
      setFilesListPageInfo((prev) => ({ ...prev, page }));
      getFileListInfo(page);
      setSelectedIds([]);
      setLastSelectedIndex(null);
    }
  };

  const getFileListInfo = async (page = 1, isPolling = false) => {
    if (!fileType) return;
    clearTimeout(listTimeoutRef.current);

    // 轮询时不显示loading
    if (!isPolling) {
      setListLoading(true);
    }

    const fileStatus = statusValue || undefined;

    // 根据是否为website-pro路径使用不同的API
    const apiCall = isWebsitePro
      ? getWebConnectorFiles(datasetId, connectorId, {
          page: page,
          size: filesListPageInfo.size,
          query: debouncedSearch.normalize('NFD'),
          file_status: fileStatus,
          start_time: rangeDate[0] || undefined,
          end_time: rangeDate[1] || undefined,
          order_by: `${reverseSortDirection ? '-' : ''}${sortBy}`,
        })
      : getDatasetsFilesList({
          id: datasetId,
          file_type: fileType,
          page: page,
          size: filesListPageInfo.size,
          query: debouncedSearch.normalize('NFD'),
          file_status: fileStatus,
          start_time: rangeDate[0] || undefined,
          end_time: rangeDate[1] || undefined,
          order_by: `${reverseSortDirection ? '-' : ''}${sortBy}`,
        });

    try {
      const res = await apiCall;
      const { items, pages, size, total } = res.data;

      // 根据数据源类型更新计数，setCount 方法会自动处理嵌套结构
      let countType: string;
      if (isWebsitePro) {
        countType = 'web-connector';
      } else {
        countType = fileType;
      }
      setCount(countType as any, total);

      setFileList(items);
      setFilesListPageInfo({ page, total, pages, size });

      removeSearchParam('status');

      // check has process
      const processArr = items.filter(
        (item: DatasetsFilesInfo) => isFileStatusProcess(item.file_status) || isFileStatusReady(item.file_status)
      );
      if (processArr.length) {
        listTimeoutRef.current = setTimeout(() => {
          getFileListInfo(page, true);
        }, 5 * 1000);
      }
    } finally {
      setListLoading(false);
      setInitLoading(false);
    }
  };

  const refreshHistoryList = () => {
    const isCruuentPageLastItem = fileList.length === 1;
    const isLastPage = filesListPageInfo.page === filesListPageInfo.pages;

    const page =
      filesListPageInfo.page > 1 && (isCruuentPageLastItem || (allSelected && isLastPage))
        ? filesListPageInfo.page - 1
        : filesListPageInfo.page;

    getFileListInfo(page);
  };

  const deleteFile = async (fileId: string) => {
    if (isCloning) {
      setCloningTipOpened(true);
      return;
    }
    setDeleteFileIds([...deleteFileIds, fileId]);
    await delDatasetsFiles({ id: datasetId, file_ids: [fileId] }).finally(() => {
      refreshHistoryList();
      setDeleteFileIds(deleteFileIds.filter((item) => item !== fileId));
    });
  };

  const handleDelete = () => {
    if (isCloning) {
      setCloningTipOpened(true);
      return;
    }
    const deleteSelected = async () => {
      setDeleteFileIds([...deleteFileIds, ...selectedIds]);
      await delDatasetsFiles({ id: datasetId, file_ids: selectedIds }).then(() => {
        refreshHistoryList();
        setDeleteFileIds([]);
        setSelectedIds([]);
      });
    };
    openModal({
      title: t('faqs.delete.title'),
      withinPortal: false,
      centered: true,
      radius: 'md',
      children: <SelectedItemRemove deleteSelected={deleteSelected} />,
    });
  };

  const onEditTitle = (item: DatasetsFilesInfo) => {
    setEditModalOpened(true);
    setEditItem(item);
  };
  const onRetrain = (item: DatasetsFilesInfo) => {
    // 对于website和website-pro都使用website的刷新接口
    refreshDatasetsWebsite({ datasetId, fileIds: [item.id] }).then(() => {
      getFileListInfo(filesListPageInfo.page);
    });
  };

  const onAdvancedTrain = (item: DatasetsFilesInfo) => {
    handleAdvancedTrain(item.id);
  };

  const handleAdvancedTrain = (fileId?: string) => {
    openModal({
      title: (
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={2}>
            <Text>{t('embeddings.documents.advanced_train.title')}</Text>
            <Text size="sm" c="gray.5">
              {t('embeddings.documents.advanced_train.desc')}
            </Text>
          </Stack>
          <CloseButton className="flex-shrink-0" onClick={() => closeAllModals()} />
        </Group>
      ),
      withCloseButton: false,
      centered: true,
      children: (
        <AdvancedTrainModal
          datasetId={datasetId}
          integrationRuleId={isWebsitePro ? connectorId : ''}
          fileList={fileList}
          fileIds={fileId ? [fileId] : selectedIds}
          onTrainSuccess={() => {
            closeAllModals();
            setSelectedIds([]);
            getFileListInfo(filesListPageInfo.page);
          }}
        />
      ),
    });
  };

  useEffect(() => {
    getFileListInfo();
    setSelectedIds([]);
    setLastSelectedIndex(null);
  }, [datasetId, debouncedSearch, statusValue, rangeDate[0], rangeDate[1], reverseSortDirection, sortBy]);

  return (
    <>
      <section className="flex flex-col relative h-full">
        <section className="flex-none">
          <Group justify="space-between" mb={20}>
            <Group className="flex-1" gap={8}>
              <Input
                size="sm"
                className="grow xl:grow-0 xl:min-w-[30%]"
                leftSection={<SvgSearch />}
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                placeholder={t('embeddings.table.search_resource_title')}
              />
              <Select
                size="sm"
                style={{ width: 120 }}
                value={statusValue}
                onChange={(value: string | null) => {
                  setStatusValue(value as ListFileStatus);
                }}
                data={[
                  { value: '', label: t('embeddings.table.filter_by_status.all') },
                  { value: 'complete', label: t('embeddings.table.filter_by_status.learned') },
                  { value: 'process', label: t('embeddings.table.filter_by_status.learning') },
                  { value: 'ready', label: t('embeddings.table.filter_by_status.ready') },
                  { value: 'fail', label: t('embeddings.table.filter_by_status.failed') },
                  { value: 'invalid', label: t('embeddings.table.filter_by_status.skipped') },
                ]}
                allowDeselect={false}
              />

              <DateSelector
                key={fileType}
                hasAll
                showCustomDateTime
                onDateChange={handleDateChange}
                radius={8}
                p={'7px 15px'}
                leftSection={<SvgCalenderGray />}
                styles={() => ({
                  root: {
                    color: '#000',
                    border: '1px solid #E4E7EC',
                    backgroundColor: '#FFF',

                    '&: hover': {
                      borderColor: '#5A4CB7',
                    },
                  },
                  leftIcon: {
                    color: 'gray',
                  },
                })}
              />
            </Group>
            <section>
              <Button
                size="sm"
                color="red"
                leftSection={<SvgTrash />}
                variant="outline"
                className="filled"
                disabled={selectedIds.length === 0}
                onClick={handleDelete}
              >
                {t('faqs.action.delete')}
              </Button>
            </section>
          </Group>
        </section>
        <section className={`${!initLoading && 'flex-auto'} relative flex flex-col`}>
          {initLoading ? (
            <TableLoading />
          ) : (
            <>
              {!fileList.length && (
                <section className="h-[400px] flex items-center justify-center min-h-[200px] bg-white rounded-[20px] overflow-hidden border border-[#F8F6FF]">
                  {t('no_data')}
                </section>
              )}
              {!!fileList.length && (
                <>
                  <section className="flex-auto">
                    <section className="h-full rounded-xl overflow-hidden">
                      <Table className={classes.table} verticalSpacing="sm" highlightOnHover captionSide="bottom">
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>
                              <Group wrap="nowrap">
                                <Checkbox checked={allSelected} onChange={() => toggleAllChecked(!allSelected)} />
                                <span>{t('embeddings.table.resource')}</span>
                              </Group>
                            </Table.Th>
                            <Table.Th>{t('embeddings.table.title')}</Table.Th>
                            <Th
                              sorted={sortBy === 'characters_count'}
                              reversed={reverseSortDirection}
                              onSort={() => setSorting('characters_count')}
                            >
                              {t('embeddings.table.characters')}
                            </Th>
                            <Table.Th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                              {t('embeddings.table.status')}
                            </Table.Th>
                            <Th
                              sorted={sortBy === 'token_count'}
                              reversed={reverseSortDirection}
                              onSort={() => setSorting('token_count')}
                            >
                              {t('embeddings.table.tokens')}
                            </Th>
                            <Th
                              sorted={sortBy === 'updated_at'}
                              reversed={reverseSortDirection}
                              onSort={() => setSorting('updated_at')}
                            >
                              {t('embeddings.table.last_modified')}
                            </Th>
                            <Table.Th style={{ whiteSpace: 'nowrap' }}>{t('embeddings.table.action')}</Table.Th>
                          </Table.Tr>
                        </Table.Thead>

                        <Table.Tbody>
                          {listLoading ? (
                            <LoadingOverlay className={'min-h-[200px]'} visible={listLoading} />
                          ) : (
                            <>
                              {fileList.map((item) => {
                                return (
                                  <TableItem
                                    key={item.id}
                                    item={item}
                                    selected={selectedIds.includes(item.id)}
                                    onSelectedChange={(shiftKey) => selectItem(item.id, shiftKey)}
                                    deleteFile={deleteFile}
                                    isFileDeleteLoading={isFileDeleteLoading}
                                    onEditTitle={() => onEditTitle(item)}
                                    onRetrain={() => onRetrain(item)}
                                    onAdvancedTrain={() => onAdvancedTrain(item)}
                                  />
                                );
                              })}
                            </>
                          )}
                        </Table.Tbody>
                      </Table>
                    </section>
                  </section>
                  <section className="flex-none">
                    {filesListPageInfo.pages > 1 && (
                      <Group className="px-2 sm:px-8 mt-5" justify="center">
                        <Pagination
                          value={filesListPageInfo.page}
                          total={filesListPageInfo.pages}
                          size={matches ? 'md' : 'sm'}
                          onChange={fileListPageOnchange}
                        />
                      </Group>
                    )}
                  </section>
                </>
              )}
            </>
          )}
        </section>
      </section>

      <Modal opened={editModalOpened} onClose={() => setEditModalOpened(false)} title={t('edit')} centered>
        {editItem && (
          <EditForm
            fileId={editItem.id}
            title={editItem.title || ''}
            onSubmitCallback={() => {
              setEditModalOpened(false);
              refreshHistoryList();
            }}
          />
        )}
      </Modal>
    </>
  );
};
